import { CSSProperties, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import Button from "@rio-cloud/rio-uikit/Button";
import TableSearch from "@rio-cloud/rio-uikit/TableSearch";
import TableToolbar from "@rio-cloud/rio-uikit/TableToolbar";
import TableViewToggles from "@rio-cloud/rio-uikit/TableViewToggles";
import { SCREEN_SM } from "@rio-cloud/rio-uikit/utils/deviceUtils";
import TableSettingsDialog from "@rio-cloud/rio-uikit/TableSettingsDialog";
import TableCardsSorting from "@rio-cloud/rio-uikit/TableCardsSorting";
import type { SortDirectionType } from "@rio-cloud/rio-uikit/SortDirection";
import type { TableColumnDetailsMap } from "@rio-cloud/rio-uikit/components/table/TableSettingsDialog.types";
import NotFoundState from "@rio-cloud/rio-uikit/NotFoundState";
import LoadMoreButton from "@rio-cloud/rio-uikit/LoadMoreButton";
import TableCol from "@rio-cloud/rio-uikit/TableCol";
import TableHead from "@rio-cloud/rio-uikit/TableHead";
import ContentLoader from "@rio-cloud/rio-uikit/ContentLoader";
import useTableSelection from "@rio-cloud/rio-uikit/useTableSelection";
import { useT } from "@hooks";
import { useStoredTableSettings } from "@hooks/useStoredTableSettings";

const RESET_BOTAO_ICONE: CSSProperties = { background: "none", border: "none", padding: 0 };

const SKELETON_ROWS = 10;
const SKELETON_CELL_HEIGHT = 18;
const SKELETON_CELL_WIDTHS = ["70%", "85%", "55%", "78%", "62%", "90%", "48%", "72%"];

export interface TollRecord {
  id: string;
  dateTime: string;
  vehicle: string;
  axles: number;
  plaza: string;
  cityState: string;
  concessionaria: string;
  amount: number;
  paymentMethod: string;
}

interface TollsTableProps {
  data: TollRecord[];
  totalData?: number;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  isLoading?: boolean;
  onViewDetail?: (id: string) => void;
  onRefresh?: () => void;
  onDownload?: () => void;
}

const formatCurrency = (value: number) =>
  `R$ ${value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

type TollColumnKey =
  "dateTime" | "vehicle" | "axles" | "plaza" | "cityState" | "concessionaria" | "amount" | "paymentMethod";

const DEFAULT_COLUMN_ORDER: TollColumnKey[] = [
  "dateTime",
  "vehicle",
  "axles",
  "plaza",
  "cityState",
  "concessionaria",
  "amount",
  "paymentMethod",
];

const DEFAULT_COLUMN_WIDTHS: Record<TollColumnKey, number> = {
  dateTime: 170,
  vehicle: 130,
  axles: 110,
  plaza: 220,
  cityState: 200,
  concessionaria: 160,
  amount: 110,
  paymentMethod: 130,
};

const renderCell = (row: TollRecord, column: TollColumnKey) => {
  if (column === "amount") return formatCurrency(row.amount);
  return row[column];
};

const sortKeyMap: Record<string, keyof TollRecord | "sortableDateTime"> = {
  dateTime: "sortableDateTime",
  vehicle: "vehicle",
  axles: "axles",
  plaza: "plaza",
  cityState: "cityState",
  concessionaria: "concessionaria",
  amount: "amount",
  paymentMethod: "paymentMethod",
};

const VISIBLE_SORT_KEY: Record<string, string> = {
  sortableDateTime: "dateTime",
};

const transformDateTime = (dateTimeStr: string) => {
  if (!dateTimeStr || dateTimeStr === "-") return "";
  const parts = dateTimeStr.split(" - ");
  if (parts.length < 2) return dateTimeStr;
  const [datePart, timePart] = parts;
  const [day, month, year] = datePart.split("/");
  return `${year}-${month}-${day}T${timePart}`;
};

export default function TollsTable({
  data,
  totalData = 0,
  onLoadMore,
  isLoadingMore = false,
  isLoading = false,
  onViewDetail,
  onRefresh,
  onDownload,
}: Readonly<TollsTableProps>) {
  const t = useT();
  const COLUMN_LABELS: Record<TollColumnKey, string> = {
    dateTime: t("tolls.table.column.dateTime"),
    vehicle: t("tolls.table.column.vehicle"),
    axles: t("tolls.table.column.axles"),
    plaza: t("tolls.table.column.plaza"),
    cityState: t("tolls.table.column.cityState"),
    concessionaria: t("tolls.table.column.concessionaria"),
    amount: t("tolls.table.column.amount"),
    paymentMethod: t("tolls.table.column.paymentMethod"),
  };

  const [searchValue, setSearchValue] = useState("");
  const [showTableSettingsDialog, setShowTableSettingsDialog] = useState(false);
  const {
    columnOrder,
    hiddenColumns,
    columnsDetails,
    viewType,
    handleColumnChange,
    handleColumnDetailsChange,
    handleViewTypeChange,
  } = useStoredTableSettings<TableColumnDetailsMap>({
    storageKey: "tolls",
    defaultColumnOrder: DEFAULT_COLUMN_ORDER,
    defaultColumnsDetails: Object.fromEntries(
      DEFAULT_COLUMN_ORDER.map((key) => [key, { width: DEFAULT_COLUMN_WIDTHS[key] }])
    ),
    forcedViewType: window.innerWidth < SCREEN_SM ? TableViewToggles.VIEW_TYPE_SINGLE_CARD : undefined,
  });

  const [localSortKey, setLocalSortKey] = useState<keyof TollRecord | "sortableDateTime" | null>("sortableDateTime");
  const [localSortDirection, setLocalSortDirection] = useState<SortDirectionType>("desc");

  const transformedData = useMemo(() => {
    return data.map((item) => ({
      ...item,
      sortableDateTime: transformDateTime(item.dateTime),
    }));
  }, [data]);

  const sortedData = useMemo(() => {
    if (!localSortKey) return transformedData;

    return [...transformedData].sort((a, b) => {
      const aValue = a[localSortKey];
      const bValue = b[localSortKey];

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === "string" && typeof bValue === "string") {
        const comparison = aValue.localeCompare(bValue);
        return localSortDirection === "asc" ? comparison : -comparison;
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        return localSortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
  }, [transformedData, localSortKey, localSortDirection]);

  const { tableRef, updateRowSelection, onActiveRowChange } = useTableSelection<TollRecord>({
    tableData: data,
    idKey: "id" as keyof TollRecord,
  });

  const visibleColumns = columnOrder.filter((column) => !hiddenColumns.includes(column)) as TollColumnKey[];

  const filteredRows = useMemo(() => {
    if (!searchValue) return sortedData;
    const term = searchValue.toLowerCase();
    return sortedData.filter((row) =>
      visibleColumns.some((col) => {
        const value = row[col];
        if (value == null) return false;

        if (col === "amount") {
          const formatted = formatCurrency(row.amount);
          return formatted.toLowerCase().includes(term);
        }

        return String(value).toLowerCase().includes(term);
      })
    );
  }, [sortedData, searchValue, visibleColumns]);

  useEffect(() => updateRowSelection(), [searchValue, updateRowSelection]);

  const handleSortChange = (event: React.MouseEvent<HTMLTableCellElement>) => {
    const newSortBy = event.currentTarget.dataset.sortby;

    if (newSortBy) {
      const newSortKey = sortKeyMap[newSortBy] || newSortBy;

      if (newSortKey === localSortKey) {
        if (localSortDirection === "asc") {
          setLocalSortDirection("desc");
        } else if (localSortDirection === "desc") {
          setLocalSortKey(null);
        }
      } else {
        setLocalSortKey(newSortKey as keyof TollRecord | "sortableDateTime");
        setLocalSortDirection("asc");
      }
    }
  };

  const handleCardSortChange = (newSortBy: string, newSortDir: SortDirectionType) => {
    setLocalSortKey(newSortBy as keyof TollRecord | "sortableDateTime");
    setLocalSortDirection(newSortDir);
  };

  const cardSortingSelectOptions = visibleColumns.map((column) => {
    const rawSortKey = sortKeyMap[column] || column;
    return {
      id: rawSortKey,
      label: COLUMN_LABELS[column],
      selected: rawSortKey === localSortKey,
      disabled: false,
    };
  });

  const tableClassNames = classNames(
    "table",
    "table-layout-fixed",
    "table-column-overflow-hidden",
    "table-bordered",
    "table-sticky",
    "table-head-filled",
    viewType === TableViewToggles.VIEW_TYPE_SINGLE_CARD && "table-cards table-single-card",
    viewType === TableViewToggles.VIEW_TYPE_MULTI_CARDS && "table-cards table-multi-cards"
  );

  return (
    <div className="height-100pct">
      <TableToolbar>
        <div className="table-toolbar-container">
          <div className="table-toolbar-group-left">
            <div className="table-toolbar-column display-flex flex-row gap-10 align-items-center">
              <TableSearch
                className="rounded-small width-100pct-xs width-250 width-300-sm"
                value={searchValue}
                onChange={(value: string) => setSearchValue(value)}
                placeholder={t("common.label.search")}
              />
            </div>
          </div>

          <div className="table-toolbar-group-right">
            <div className="table-toolbar-column table-toolbar-column-spacer">
              <TableViewToggles
                initialViewType={viewType}
                className="rounded-small"
                onViewTypeChange={handleViewTypeChange}
                tableViewTooltipContent={t("table.toggle.table")}
                singleCardViewTooltipContent={t("table.toggle.list")}
                multiCardsViewTooltipContent={t("table.toggle.cards")}
              />
            </div>
            <div className="table-toolbar-column">
              <div className="btn-group rounded-small">
                <Button iconOnly iconName="rioglyph rioglyph-refresh" onClick={onRefresh} />
                <Button iconOnly iconName="rioglyph rioglyph-download" onClick={onDownload} />
                <Button
                  iconOnly
                  iconName="rioglyph rioglyph-cog"
                  onClick={() => setShowTableSettingsDialog((prev) => !prev)}
                />
              </div>
            </div>
          </div>

          {showTableSettingsDialog && (
            <TableSettingsDialog
              show={showTableSettingsDialog}
              title={t("table.settings.title")}
              onHide={() => setShowTableSettingsDialog(false)}
              onColumnChange={handleColumnChange}
              defaultColumnOrder={DEFAULT_COLUMN_ORDER}
              defaultHiddenColumns={[]}
              columnOrder={columnOrder}
              hiddenColumns={hiddenColumns}
              columnLabels={COLUMN_LABELS}
              disabledColumns={[]}
              closeButtonText={t("table.settings.close")}
              resetButtonText={t("table.settings.reset")}
              searchPlaceholder={t("table.settings.columnName")}
              notFoundMessage={t("table.settings.noColumns")}
              columnsDetails={columnsDetails}
              autoLabel={t("table.settings.auto")}
              onColumnDetailsChange={handleColumnDetailsChange}
              onSearchChange={() => {}}
              immediateChange
            />
          )}
        </div>
      </TableToolbar>

      {localSortKey && viewType !== TableViewToggles.VIEW_TYPE_TABLE && (
        <TableCardsSorting
          className="display-none display-block-sm"
          selectOptions={cardSortingSelectOptions}
          sortName={localSortKey}
          sortOrder={localSortDirection}
          onSortChange={handleCardSortChange}
        />
      )}

      <div className="table-responsive min-height-300">
        {!isLoading && filteredRows.length === 0 && viewType !== TableViewToggles.VIEW_TYPE_TABLE && (
          <NotFoundState message={t("tolls.table.notFound")} outerClassName="bg-none" />
        )}
        <table ref={tableRef} className={tableClassNames}>
          <colgroup>
            {visibleColumns.map((column) => (
              <TableCol
                key={column}
                columnDetails={columnsDetails[column] ?? { width: DEFAULT_COLUMN_WIDTHS[column] }}
              />
            ))}
            <TableCol className="table-action" />
          </colgroup>
          <thead>
            <tr>
              {visibleColumns.map((column) => {
                const sortKeyString = localSortKey || "";
                const displaySortKey = sortKeyString ? VISIBLE_SORT_KEY[sortKeyString] || sortKeyString : "";

                return (
                  <TableHead
                    key={column}
                    label={COLUMN_LABELS[column]}
                    column={column}
                    sortBy={displaySortKey}
                    sortDir={localSortKey ? localSortDirection : undefined}
                    onClick={handleSortChange}
                  />
                );
              })}
              <TableHead className="table-action" />
            </tr>
          </thead>
          <tbody>
            {isLoading &&
              Array.from({ length: SKELETON_ROWS }, (_, rowIndex) => (
                <tr key={`skeleton-${rowIndex}`}>
                  {visibleColumns.map((column, columnIndex) => (
                    <td key={column} data-field={COLUMN_LABELS[column]}>
                      <ContentLoader
                        className="display-block"
                        height={SKELETON_CELL_HEIGHT}
                        width={SKELETON_CELL_WIDTHS[columnIndex % SKELETON_CELL_WIDTHS.length]}
                      />
                    </td>
                  ))}
                  <td className="table-action" />
                </tr>
              ))}
            {!isLoading && filteredRows.length === 0 && viewType === TableViewToggles.VIEW_TYPE_TABLE && (
              <tr className="bg-none">
                <td colSpan={visibleColumns.length + 1}>
                  <NotFoundState outerClassName="border-none bg-none" message={t("tolls.table.notFound")} />
                </td>
              </tr>
            )}
            {!isLoading &&
              filteredRows.map((row) => (
                <tr key={row.id} data-id={row.id} onClick={onActiveRowChange} className="cursor-pointer">
                  {visibleColumns.map((column) => (
                    <td key={column} data-field={COLUMN_LABELS[column]}>
                      {renderCell(row, column)}
                    </td>
                  ))}
                  <td className="table-action">
                    <button
                      type="button"
                      className="rioglyph rioglyph-info-sign text-size-18 text-color-primary cursor-pointer"
                      style={RESET_BOTAO_ICONE}
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewDetail?.(row.id);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          onViewDetail?.(row.id);
                        }
                      }}
                      title={t("tolls.table.action.viewDetail")}
                    />
                  </td>
                </tr>
              ))}
            <tr className="table-card-placeholder" />
            <tr className="table-card-placeholder" />
            <tr className="table-card-placeholder" />
            <tr className="table-card-placeholder" />
            <tr className="table-card-placeholder" />
          </tbody>
        </table>
      </div>

      {data.length > 0 && (
        <LoadMoreButton
          loaded={data.length}
          total={totalData}
          onLoadMore={() => onLoadMore?.()}
          loadMoreMessage={isLoadingMore ? t("common.label.loading") : t("common.action.loadMore")}
          noMoreMessage={t("tolls.table.loadMore.noMore")}
          isInteractive={!isLoadingMore}
        />
      )}
    </div>
  );
}
